import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';
import { hardnessBaseline, cityKey } from '../data/hardnessBaseline.js';
import { STATES_INDEX } from '../data/states/index.js';
import { classifyHardness as classifyBand, HARDNESS_BANDS } from '../data/water.js';

// ─────────────────────────────────────────────────────────────────────────
// THE REGIONAL PROTOCOL GENERATOR
// Window Washing Guide — Tool 09 of 09
// ─────────────────────────────────────────────────────────────────────────
//
// First tool in the build order to diverge structurally from the
// question-tree pattern. Where TintIdentifier, FrameSubstrateIdentifier,
// and IGUSelfCheck are "answer-six-questions, get-a-verdict" diagnostics,
// the Regional Protocol Generator is fundamentally an INPUT-AND-SYNTHESIS
// tool. The user enters state + city + use case; the tool synthesizes the
// regional data already present in src/data/states/<slug>.js into a
// per-region customized cleaning protocol document.
//
// The output is not a single verdict but a structured multi-section
// document:
//   • The water you're working with     — waterProfile + city hardness
//   • The climate context                — climate zones + dominant season
//   • Seasonal contaminants              — regionalContaminants, ranked
//   • The chemistry recommendation       — derived from hardness tier
//   • The protocol                       — sequenced steps for this region
//   • What to watch for                  — extremes + edge cases
//   • The cleaning calendar              — current-season excerpt
//
// Brand: matches TintIdentifier / FrameSubstrateIdentifier / IGUSelfCheck
// exactly on design tokens, fonts, animation primitives, dark mode toggle,
// and numbered header. The verdict UI diverges (multi-section document
// rather than panel-based verdict) because the domain demands it. This
// is the same template-flex pattern established in s52 — keep the design
// language, let verdict UI evolve with domain.
//
// Data sources: src/data/states/<slug>.js (loaded dynamically). Each state
// record carries waterProfile, topCities (with hardness + utility per
// city), climate, regionalContaminants, housingStock, cleaningCalendar,
// and linkArticles. These are the same records that drive StatePage and
// CityPage; the protocol synthesis here is the first surface that
// combines all of them into a single user-facing artifact.
//
// Methodology citations: USGS Water Hardness Survey (2023), EPA municipal
// CCR data, USDA-NRCS regional climate normals, plus the regional
// contributor articles that documented contaminant patterns for each
// state's editorial beat.
//
// ─────────────────────────────────────────────────────────────────────────

// ─── Use cases ────────────────────────────────────────────────────────────
// Four use cases that meaningfully change protocol shape. The use case
// is not a verdict-determining factor (the region drives that); it is
// a filter that tunes which sections of the protocol are emphasized.
const USE_CASES = [
  {
    id: 'residential-diy',
    label: 'Residential — DIY homeowner',
    desc: "Your own house. You want the right protocol; you don't need pro pricing or commercial scheduling.",
  },
  {
    id: 'residential-pro',
    label: 'Residential — working cleaner',
    desc: 'Customer route. You need the chemistry, the scheduling guidance, and the regional contaminant calendar.',
  },
  {
    id: 'commercial',
    label: 'Commercial / property management',
    desc: 'Storefronts, office buildings, or recurring commercial work. Frequency and labor structure are the priorities.',
  },
  {
    id: 'heritage',
    label: 'Heritage residential — pre-1925 stock',
    desc: 'Historic single-pane wood-sash work. Gentler chemistries, no razor work, conservation-grade handling.',
  },
];

// ─── Hardness tier classification (matches HardWaterScorer) ──────────────
// s73 — the comment that used to sit here claimed "Standard USGS
// classification, kept in lockstep with the scorer." It was neither. It used
// `<` where the scorer used `<=` (off-by-one on all four boundaries), and it
// labelled 61–120 mg/L "moderate" where USGS and the scorer both say
// "Moderately Hard". This tool renders that label into the sentence
// "Classified as <label> by USGS scale" — so it was attributing a word USGS
// does not use to USGS, in prose, on the page.
//
// Now the one classifier. `tier` keeps the kebab keys CHEMISTRY_BY_TIER is
// keyed by; `label` is the band's real name, lowercased for mid-sentence use.
// Total, like the copy it replaces: the call site indexes CHEMISTRY_BY_TIER
// with the result and renders .label, so null would crash. Non-finite input is
// already unreachable (verify-water-atlas fails the build on a non-numeric
// state range, and effectiveHardness is a city reading or a midpoint of two
// gated numbers) — the fallback just holds the old contract.
function classifyHardness(mgL) {
  const band = classifyBand(mgL) || HARDNESS_BANDS[HARDNESS_BANDS.length - 1];
  return { tier: band.key, label: band.label.toLowerCase() };
}

// ─── Chemistry recommendation by hardness tier ────────────────────────────
// The chemistry call is a function of the hardness tier first, then the
// use case modulates the protocol around it. The text here is intentionally
// reusable across states because the chemistry decision IS regional only
// through hardness — the heritage and pro adjustments come from the use
// case filter, not from the state record.
const CHEMISTRY_BY_TIER = {
  'soft': {
    headline: 'The House Standard recipe will perform here, undistilled if you prefer.',
    body: "Soft water tap supply is forgiving enough that the standard surfactant-and-water recipe works as-is. A distilled rinse is unnecessary on most jobs; reserve it for dark glass or south-facing residential where any residue would be visible in the worst light.",
    pro: 'Pro tip: in soft-water markets, save the distilled budget for the high-visibility surfaces and run tap on everything else. The economics matter on a residential route.',
  },
  'moderate': {
    headline: 'House Standard recipe, with a distilled rinse on dark and south-facing glass.',
    body: "Moderate hardness will leave faint residue on dark or south-facing glass within a few weeks of cleaning. The fix is a final distilled rinse on the visible surfaces; tap is fine for the rest. Build the distilled jug into your van load on every residential job.",
    pro: 'Pro tip: customers in moderate-water markets often think they have a streaking problem when they have a hardness problem — the distilled rinse is the diagnostic and the cure simultaneously.',
  },
  'hard': {
    headline: 'Distilled rinse on every pane. Cut the surfactant load by a quarter.',
    body: "Hard water deposits accelerate the rate of visible spotting and will defeat the standard recipe within a month on outdoor glass. Distilled rinse becomes mandatory rather than optional, and the surfactant load wants to come down slightly (about 25%) because the residual surfactant interacts with the harder rinse water on the squeegee.",
    pro: 'Pro tip: hard-water customers see results most clearly after a deep-clean reset followed by the distilled-rinse maintenance protocol. Quote the reset separately from the maintenance frequency.',
  },
  'very-hard': {
    headline: 'Distilled rinse, deep-clean reset, monthly maintenance frequency.',
    body: "Very hard water creates a maintenance schedule rather than a one-time cleaning protocol. Initial cleaning includes a deep-clean reset to remove accumulated mineral haze; subsequent visits are on a tighter cycle (monthly to bimonthly on outdoor residential) to prevent the haze from re-establishing. The chemistry is unchanged from the hard-water tier; the change is in cadence.",
    pro: 'Pro tip: the cost structure on very-hard-water routes is monthly-frequency-led. A quarterly customer in San Antonio will be unhappy with their windows by month three; a monthly customer will be reliably happy.',
  },
  'extremely-hard': {
    headline: 'DI filtration or pure-water pole system; never tap-rinse at this hardness.',
    body: "Extremely hard supply (typically deep-aquifer or well systems) will form visible mineral deposits in a single cleaning cycle if tap water touches the glass. The protocol shifts to a pure-water pole system (deionized or reverse-osmosis filtered) for the entire wash — both wash and rinse — rather than a tap-wash-and-distilled-rinse. Standalone distilled rinse is insufficient at this hardness level.",
    pro: 'Pro tip: extremely-hard markets are where the pure-water pole investment pays back fastest. The hardware cost is recovered in distilled-water savings within a season of regular residential routing.',
  },
};

// ─── Use-case modifiers to the protocol ───────────────────────────────────
// Each use case adds one to three protocol-shaping notes that ride on top
// of the chemistry recommendation. These are NOT chemistry changes — they
// are scheduling, labor, and special-handling shifts.
const USE_CASE_NOTES = {
  'residential-diy': [
    [
      'Cadence is yours to set',
      "DIY owners can run the protocol at whatever frequency the visible result demands. Most homeowners find that twice a year is the floor; quarterly is comfortable in most regions; monthly is overkill except in the very-hard water markets.",
    ],
    [
      'Equipment investment is modest',
      "A squeegee, a strip washer, a distilled-water jug, and the right chemistry will get a DIY homeowner through any region in this dataset. The pure-water pole system is a pro-only investment.",
    ],
  ],
  'residential-pro': [
    [
      'Quote the frequency, not the cleaning',
      'On a working residential route, the customer is buying a maintenance frequency, not a one-time cleaning. Set the cadence to match the regional hardness and seasonal contaminant pattern; quote against that cadence.',
    ],
    [
      'Plan the seasonal contaminant calendar',
      'The pollen and overspray patterns documented in this regional profile will shape the highest-volume weeks of your year. Book those weeks first, charge appropriately, and protect them from rescheduling drift.',
    ],
    [
      'Build pure-water capacity for the harder markets',
      'If your route includes very-hard or extremely-hard water households, a pure-water pole system pays for itself within a season. In moderate or soft markets, the distilled-jug protocol is enough.',
    ],
  ],
  'commercial': [
    [
      'Frequency is the contract',
      'Commercial work runs on a fixed-frequency contract. The regional hardness tier drives whether that frequency is weekly, biweekly, monthly, or quarterly; storefront visibility usually pushes to the more-frequent end of that range.',
    ],
    [
      'Storefront chemistry is the same as residential',
      "Despite the volume difference, commercial storefront chemistry is unchanged from residential — same recipe, same distilled rinse where the water demands it. The change is in labor structure: per-pane time targets and route density rather than per-house custom work.",
    ],
    [
      'Interior winter work is the off-season buffer',
      "In any market with a seasonal weather constraint on exterior work, commercial interior work is the off-season revenue buffer. Build the commercial book to lean interior in the constrained months.",
    ],
  ],
  'heritage': [
    [
      'No ammonia. No acid. No razor work.',
      "Pre-1925 single-pane wood-sash work tolerates none of the aggressive chemistries that work on modern coated glass. The protocol is a gentle pH-neutral surfactant with a soft cloth or a low-pressure squeegee, never a razor (which can score the historic glass surface).",
    ],
    [
      'The glazing putty is part of the window',
      "Original glazing putty around the glass perimeter is a structural element, not a finish. Avoid soaking it; avoid getting solvent on it; avoid pressure-washing within six inches of it. A failed putty bead is an order-of-magnitude more expensive repair than a poorly-cleaned window.",
    ],
    [
      "The wood sash wants drier work",
      "Wood sash, especially with original glazing, wants drier work than modern vinyl or aluminum. Wring the strip washer; squeegee promptly; do not let standing water sit at the bottom rail. The seasonal exception is the storm-window assembly, which is its own protocol.",
    ],
    [
      'When in doubt, consult conservation guidance',
      'For pre-1900 cylinder or crown glass, modern protocols can damage surfaces that look identical to modern glass. The historic-glass article is the right reference; a local preservation society is the right phone call for high-value pieces.',
    ],
  ],
};

// ─── Current-season detection ────────────────────────────────────────────
// The cleaning calendar in each state record breaks the year into four
// seasons; we surface the relevant one as the "this is what your year
// looks like right now" panel. Edge dates use a rough mid-month boundary.
function currentSeason(date) {
  const m = date.getMonth();
  // Northern hemisphere meteorological seasons:
  // spring: Mar-May (2-4), summer: Jun-Aug (5-7),
  // fall:   Sep-Nov (8-10), winter: Dec-Feb (11, 0, 1)
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'fall';
  return 'winter';
}

// ─── Contaminant severity sort ────────────────────────────────────────────
// Order contaminants severe > moderate > mild for display. Ordering is by
// severity descending, then alphabetical by name as tiebreaker so the
// display is deterministic for any given state.
const SEVERITY_RANK = { severe: 3, moderate: 2, mild: 1 };
function sortContaminants(list) {
  return [...list].sort((a, b) => {
    const da = SEVERITY_RANK[a.severity] || 0;
    const db = SEVERITY_RANK[b.severity] || 0;
    if (db !== da) return db - da;
    return a.name.localeCompare(b.name);
  });
}

// ─── Synthesis ────────────────────────────────────────────────────────────
// Pull the state record + city (or state-default) + use case into the
// structured protocol document. The synthesis is deterministic: same
// inputs produce the same output. The protocol is sectioned so the
// render can iterate cleanly.
function synthesize({ stateRecord, citySlug, useCaseId }) {
  // Resolve the city. If the user picked a specific city, use its
  // hardness reading; otherwise we use the state's dominant tier as a
  // best-guess regional default and label it as such.
  let city = null;
  if (citySlug && stateRecord.topCities) {
    city = stateRecord.topCities.find(
      (c) => cityKey(c) === citySlug
    ) || null;
  }

  const baseline = hardnessBaseline(stateRecord, city); // s136 (#58): published figure first, never a planning value credited to a utility
  const effectiveHardness = baseline.mgL;
  const tier = classifyHardness(effectiveHardness);
  const chemistry = CHEMISTRY_BY_TIER[tier.tier];
  const useCase = USE_CASES.find((u) => u.id === useCaseId);
  const useCaseNotes = USE_CASE_NOTES[useCaseId] || [];

  const season = currentSeason(new Date());
  const seasonText = stateRecord.cleaningCalendar?.[season] || '';

  const contaminants = sortContaminants(stateRecord.regionalContaminants || []);

  return {
    state: stateRecord,
    city,
    effectiveHardness,
    baseline,
    tier,
    chemistry,
    useCase,
    useCaseNotes,
    season,
    seasonText,
    contaminants,
  };
}

// ─── Component ────────────────────────────────────────────────────────────
export default function RegionalProtocolGenerator() {
  // s136 (Phase 6, tools pre-filled): ?state=<slug>&city=<city-slug> opens the tool on that place, so a
  // city or state page can deep-link here. Unknown slugs are ignored (the picker starts empty).
  const [stateSlug, setStateSlug] = useState(() => {
    const v = typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('state') || '';
    return STATES_INDEX.some((s) => s.slug === v) ? v : '';
  });
  const [citySlug, setCitySlug] = useState(() => (typeof window === 'undefined' ? '' : (new URLSearchParams(window.location.search).get('city') || '').replace(/[^a-z0-9-]/g, '')));
  const [useCaseId, setUseCaseId] = useState('');
  const [stateRecord, setStateRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [protocol, setProtocol] = useState(null);
  const [dark, setDark] = useState(false);

  // Sort states alphabetically for the dropdown.
  const sortedStates = useMemo(
    () => [...STATES_INDEX].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  // Load the state record when stateSlug changes. The state files live in
  // src/data/states/<slug>.js and are imported dynamically so the tool's
  // own chunk stays small.
  useEffect(() => {
    if (!stateSlug) {
      setStateRecord(null);
      setCitySlug('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    import(`../data/states/${stateSlug}.js`)
      .then((mod) => {
        if (cancelled) return;
        setStateRecord(mod.default);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('Failed to load state record:', stateSlug, err);
        setStateRecord(null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [stateSlug]);

  const cities = stateRecord?.topCities || [];
  const canGenerate = stateRecord && useCaseId && !loading;

  function generate() {
    if (!canGenerate) return;
    setProtocol(synthesize({ stateRecord, citySlug, useCaseId }));
  }

  function reset() {
    setProtocol(null);
    setStateSlug('');
    setCitySlug('');
    setUseCaseId('');
    setStateRecord(null);
  }

  // ─── Design tokens ──────────────────────────────────────────────────
  // Identical to TintIdentifier / FrameSubstrateIdentifier / IGUSelfCheck.
  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  return (
    <>
      <StaticSeo schemaType="webApplication" />
    <div
      style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }
        .reveal-5 { animation-delay: 0.45s; }
        .reveal-6 { animation-delay: 0.55s; }
        .reveal-7 { animation-delay: 0.65s; }
        .input-field {
          background: transparent;
          border: 1px solid ${T.rule};
          border-left: 2px solid transparent;
          padding: 18px 22px;
          color: ${T.ink};
          font-family: ${fontBody};
          font-size: 17px;
          width: 100%;
          appearance: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .input-field:focus {
          outline: none;
          border-left-color: ${T.sienna};
        }
        .input-field:hover {
          border-left-color: ${T.sienna};
        }
        .input-field:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .use-case-tile {
          animation: slideIn 0.4s cubic-bezier(.2,.7,.3,1) both;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .use-case-tile:nth-child(1) { animation-delay: 0.05s; }
        .use-case-tile:nth-child(2) { animation-delay: 0.10s; }
        .use-case-tile:nth-child(3) { animation-delay: 0.15s; }
        .use-case-tile:nth-child(4) { animation-delay: 0.20s; }
        .use-case-tile:hover {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
          transform: translateX(2px);
        }
        .use-case-tile.selected {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
        }
        .ghost-btn:hover { color: ${T.sienna}; }
        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }
        .generate-btn:not(:disabled):hover { background: ${T.ink}; color: ${T.bg}; }

        @media (max-width: 720px) {
          .h1-title { font-size: 36px !important; line-height: 1.05 !important; }
          .section-title { font-size: 26px !important; }
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Header */}
        <header
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 20, borderBottom: `1px solid ${T.rule}`, marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32, color: T.inkSoft,
              cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Title */}
        <div className="reveal reveal-1">
          <div
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; 09 / 09 &nbsp;·&nbsp; REGIONAL SYNTHESIS
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
              lineHeight: 1.04, letterSpacing: '-0.025em',
              margin: '0 0 24px', maxWidth: 800,
            }}
          >
            The Regional Protocol{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Generator
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft,
              maxWidth: 620, margin: '0 0 12px',
            }}
          >
            Pick your state, your city, and the work you're doing. The tool synthesizes
            water chemistry, climate, seasonal contaminants, and housing-stock norms into
            a custom cleaning protocol for your region.
          </p>

          <p
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
            }}
          >
            Synthesis pulls from USGS hardness data, EPA municipal CCRs, and the regional editorial beats for each state.
          </p>
        </div>

        {/* INPUT PHASE */}
        {!protocol && (
          <div>
            {/* State selector */}
            <div className="reveal reveal-2" style={{ marginBottom: 28 }}>
              <label
                htmlFor="state-select"
                style={{
                  display: 'block',
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.inkFaint,
                  marginBottom: 10,
                }}
              >
                STEP 01 &nbsp;·&nbsp; YOUR STATE
              </label>
              <select
                id="state-select"
                className="input-field"
                value={stateSlug}
                onChange={(e) => {
                  setStateSlug(e.target.value);
                  setCitySlug('');
                }}
              >
                <option value="">Select a state…</option>
                {sortedStates.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* City selector — appears once state is chosen */}
            {stateSlug && (
              <div className="reveal reveal-3" style={{ marginBottom: 28 }}>
                <label
                  htmlFor="city-select"
                  style={{
                    display: 'block',
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.inkFaint,
                    marginBottom: 10,
                  }}
                >
                  STEP 02 &nbsp;·&nbsp; YOUR CITY{' '}
                  <span style={{ color: T.inkFaint, letterSpacing: '0.05em' }}>
                    — or skip for the state-default reading
                  </span>
                </label>
                <select
                  id="city-select"
                  className="input-field"
                  value={citySlug}
                  onChange={(e) => setCitySlug(e.target.value)}
                  disabled={loading || !stateRecord}
                >
                  <option value="">
                    {loading
                      ? 'Loading…'
                      : `Use the ${stateRecord?.name || 'state'} regional default`}
                  </option>
                  {cities.map((c) => {
                    const slug = cityKey(c);
                    return (
                      <option key={slug} value={slug}>
                        {c.name} &nbsp;—&nbsp; {c.hardness} mg/L
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Use case tiles — appear once state is loaded */}
            {stateRecord && !loading && (
              <div className="reveal reveal-4" style={{ marginBottom: 40 }}>
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.inkFaint,
                    marginBottom: 10,
                  }}
                >
                  STEP 03 &nbsp;·&nbsp; WHAT KIND OF WORK
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {USE_CASES.map((uc, i) => (
                    <button
                      key={uc.id}
                      className={`use-case-tile${useCaseId === uc.id ? ' selected' : ''}`}
                      onClick={() => setUseCaseId(uc.id)}
                      style={{
                        background: useCaseId === uc.id ? T.surface : 'transparent',
                        border: `1px solid ${T.rule}`,
                        borderLeft: `2px solid ${useCaseId === uc.id ? T.sienna : 'transparent'}`,
                        padding: '20px 24px',
                        cursor: 'pointer',
                        fontFamily: fontBody,
                        color: T.ink, textAlign: 'left',
                        display: 'grid',
                        gridTemplateColumns: '32px 1fr',
                        gap: 16, alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: fontMono, fontSize: 10,
                          letterSpacing: '0.15em', color: T.inkFaint,
                        }}
                      >
                        № 0{i + 1}
                      </div>
                      <div>
                        <div style={{ fontFamily: fontDisplay, fontSize: 19, marginBottom: 4 }}>
                          {uc.label}
                        </div>
                        <div style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.4 }}>
                          {uc.desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Generate button */}
            {stateRecord && useCaseId && !loading && (
              <div className="reveal reveal-5" style={{ marginBottom: 40 }}>
                <button
                  className="generate-btn"
                  onClick={generate}
                  disabled={!canGenerate}
                  style={{
                    background: T.sienna,
                    color: T.bg,
                    border: 'none',
                    fontFamily: fontMono, fontSize: 12,
                    letterSpacing: '0.18em',
                    padding: '18px 36px',
                    cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  GENERATE THE PROTOCOL →
                </button>
              </div>
            )}
          </div>
        )}

        {/* OUTPUT PHASE — synthesized protocol document */}
        {protocol && (
          <div>
            {/* Document header */}
            <div className="reveal reveal-1">
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◆ PROTOCOL &nbsp;·&nbsp; {protocol.tier.label.toUpperCase()} WATER &nbsp;·&nbsp; {protocol.state.region.toUpperCase()}
              </div>

              <h2
                style={{
                  fontFamily: fontDisplay, fontSize: 44, fontWeight: 400,
                  lineHeight: 1.05, letterSpacing: '-0.025em',
                  margin: '0 0 6px',
                }}
              >
                {protocol.city ? protocol.city.name : protocol.state.name},{' '}
                <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
                  {protocol.state.abbreviation}
                </em>
              </h2>
              <div
                style={{
                  fontFamily: fontDisplay, fontStyle: 'italic',
                  fontSize: 21, color: T.sienna,
                  marginBottom: 24,
                }}
              >
                {protocol.useCase.label.toLowerCase()}
              </div>

              <p
                style={{
                  fontFamily: fontDisplay, fontSize: 18, fontWeight: 300,
                  lineHeight: 1.6, color: T.inkSoft,
                  maxWidth: 640, margin: '0 0 32px',
                }}
              >
                {protocol.state.waterProfile.summary}
              </p>
            </div>

            {/* SECTION 1 — The water */}
            <ProtocolSection
              T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
              kicker="◐ THE WATER YOU'RE WORKING WITH"
              kickerColor={T.sage}
              title={protocol.baseline.title}
              borderColor={T.sage}
              revealClass="reveal reveal-2"
            >
              <p style={{ fontFamily: fontBody, fontSize: 15, lineHeight: 1.65, color: T.ink, margin: '0 0 12px' }}>
                {protocol.city ? protocol.city.notes : protocol.state.waterProfile.notes}
              </p>
              <p style={{ fontFamily: fontBody, fontSize: 13, lineHeight: 1.55, color: T.inkSoft, margin: 0, fontStyle: 'italic' }}>
                Source: {protocol.city?.sourceType ? `${protocol.city.sourceType} water` : `${protocol.state.waterProfile.dominantSource} sources`}. Classified as <strong style={{ fontStyle: 'normal', color: T.ink }}>{protocol.tier.label}</strong> by USGS scale.
              </p>
            </ProtocolSection>

            {/* SECTION 2 — Climate context */}
            <ProtocolSection
              T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
              kicker="❉ THE CLIMATE CONTEXT"
              kickerColor={T.sienna}
              title={protocol.state.climate?.zones?.[0] || 'Regional climate'}
              borderColor={T.sienna}
              revealClass="reveal reveal-3"
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', marginBottom: 16 }}>
                {protocol.state.climate?.summer && (
                  <>
                    <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint, paddingTop: 4 }}>SUMMER</div>
                    <div style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.55, color: T.ink }}>{protocol.state.climate.summer}</div>
                  </>
                )}
                {protocol.state.climate?.winter && (
                  <>
                    <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint, paddingTop: 4 }}>WINTER</div>
                    <div style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.55, color: T.ink }}>{protocol.state.climate.winter}</div>
                  </>
                )}
                {protocol.state.climate?.humidityProfile && (
                  <>
                    <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint, paddingTop: 4 }}>HUMIDITY</div>
                    <div style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.55, color: T.ink }}>{protocol.state.climate.humidityProfile}</div>
                  </>
                )}
                {protocol.state.climate?.extremes && (
                  <>
                    <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint, paddingTop: 4 }}>EXTREMES</div>
                    <div style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.55, color: T.ink }}>{protocol.state.climate.extremes}</div>
                  </>
                )}
              </div>
            </ProtocolSection>

            {/* SECTION 3 — Seasonal contaminants */}
            {protocol.contaminants.length > 0 && (
              <ProtocolSection
                T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
                kicker="◊ SEASONAL CONTAMINANTS"
                kickerColor={T.sienna}
                title={`${protocol.contaminants.length} known regional patterns, ranked by severity`}
                borderColor={T.sienna}
                revealClass="reveal reveal-4"
              >
                {protocol.contaminants.map((c, i) => (
                  <div
                    key={c.name}
                    style={{
                      padding: '14px 0',
                      borderTop: i === 0 ? 'none' : `1px solid ${T.rule}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: fontDisplay, fontSize: 17, color: T.ink }}>
                        {c.name}
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                        <span style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint }}>
                          {c.season?.toUpperCase()}
                        </span>
                        <span style={{
                          fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em',
                          color: c.severity === 'severe' ? T.sienna : c.severity === 'moderate' ? T.ink : T.inkFaint,
                          padding: '2px 8px',
                          border: `1px solid ${c.severity === 'severe' ? T.sienna : T.rule}`,
                        }}>
                          {c.severity?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontFamily: fontBody, fontSize: 13, color: T.inkSoft, lineHeight: 1.5 }}>
                      {c.notes}
                    </div>
                  </div>
                ))}
              </ProtocolSection>
            )}

            {/* SECTION 4 — Chemistry recommendation */}
            <ProtocolSection
              T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
              kicker="⚗ THE CHEMISTRY"
              kickerColor={T.sienna}
              title={protocol.chemistry.headline}
              borderColor={T.sienna}
              revealClass="reveal reveal-5"
            >
              <p style={{ fontFamily: fontBody, fontSize: 15, lineHeight: 1.65, color: T.ink, margin: '0 0 16px' }}>
                {protocol.chemistry.body}
              </p>
              <div
                style={{
                  fontFamily: fontDisplay, fontSize: 14,
                  lineHeight: 1.55, color: T.inkSoft,
                  fontStyle: 'italic',
                  paddingLeft: 16,
                  borderLeft: `2px solid ${T.rule}`,
                }}
              >
                {protocol.chemistry.pro}
              </div>
            </ProtocolSection>

            {/* SECTION 5 — Use-case protocol notes */}
            <ProtocolSection
              T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
              kicker="→ THE PROTOCOL — TUNED TO YOUR WORK"
              kickerColor={T.sienna}
              title={`What ${protocol.useCase.label.toLowerCase()} adds on top of the chemistry call`}
              borderColor={T.sienna}
              revealClass="reveal reveal-6"
            >
              {protocol.useCaseNotes.map(([name, note], i) => (
                <div
                  key={i}
                  style={{
                    padding: '12px 0',
                    borderTop: i === 0 ? 'none' : `1px solid ${T.rule}`,
                  }}
                >
                  <div style={{ fontFamily: fontDisplay, fontSize: 16, color: T.ink, marginBottom: 4 }}>
                    {name}
                  </div>
                  <div style={{ fontFamily: fontBody, fontSize: 13, color: T.inkSoft, lineHeight: 1.5 }}>
                    {note}
                  </div>
                </div>
              ))}
            </ProtocolSection>

            {/* SECTION 6 — Housing stock context */}
            {protocol.state.housingStock && (
              <ProtocolSection
                T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
                kicker="▣ HOUSING STOCK CONTEXT"
                kickerColor={T.sage}
                title="What you're likely to be cleaning"
                borderColor={T.sage}
                revealClass="reveal reveal-7"
              >
                <p style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.6, color: T.ink, margin: 0 }}>
                  {protocol.state.housingStock}
                </p>
              </ProtocolSection>
            )}

            {/* SECTION 7 — This-season calendar */}
            {protocol.seasonText && (
              <ProtocolSection
                T={T} fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
                kicker={`◷ THIS SEASON — ${protocol.season.toUpperCase()}`}
                kickerColor={T.sienna}
                title="What your year looks like right now"
                borderColor={T.sienna}
                revealClass="reveal reveal-7"
              >
                <p style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.6, color: T.ink, margin: '0 0 12px' }}>
                  {protocol.seasonText}
                </p>
                {/* Tiny mini-calendar of the other seasons for context */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 16 }}>
                  {['spring', 'summer', 'fall', 'winter'].filter((s) => s !== protocol.season && protocol.state.cleaningCalendar?.[s]).map((s) => (
                    <div key={s} style={{ paddingTop: 8, borderTop: `1px solid ${T.rule}` }}>
                      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint, marginBottom: 6 }}>
                        {s.toUpperCase()}
                      </div>
                      <div style={{ fontFamily: fontBody, fontSize: 12, color: T.inkSoft, lineHeight: 1.45 }}>
                        {protocol.state.cleaningCalendar[s].split('. ').slice(0, 1).join('.') + '.'}
                      </div>
                    </div>
                  ))}
                </div>
              </ProtocolSection>
            )}

            {/* Actions */}
            <div
              className="reveal reveal-7"
              style={{
                display: 'flex', gap: 12, flexWrap: 'wrap',
                marginTop: 8, marginBottom: 40,
              }}
            >
              <button
                onClick={reset}
                className="pill-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${T.ink}`,
                  color: T.ink,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  padding: '14px 28px', cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                ↺ GENERATE FOR ANOTHER REGION
              </button>
              <button
                onClick={() => window.print()}
                className="pill-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${T.ink}`,
                  color: T.ink,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  padding: '14px 28px', cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                PRINT THIS PROTOCOL →
              </button>
            </div>

            {/* Cross-link grid — pulls related state pages + linked articles */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 0,
                borderTop: `1px solid ${T.rule}`,
                marginBottom: 40,
              }}
            >
              <Link
                to={`/states/${protocol.state.slug}`}
                className="ghost-btn"
                style={{
                  padding: '20px 0',
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                  BY REGION
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                  Full {protocol.state.name} guide
                </div>
              </Link>
              {protocol.city?.citySlug && (
                <Link
                  to={`/states/${protocol.state.slug}/${protocol.city.citySlug}`}
                  className="ghost-btn"
                  style={{
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                    CITY PAGE
                  </div>
                  <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                    {protocol.city.name} — full city profile
                  </div>
                </Link>
              )}
              <Link
                to="/tools/hard-water-scorer"
                className="ghost-btn"
                style={{
                  padding: '20px 0',
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                  THE TOOLS
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                  Score your ZIP — the precise hardness reading
                </div>
              </Link>
              {protocol.state.linkArticles?.slice(0, 1).map((slug) => (
                <Link
                  key={slug}
                  to={`/articles/${slug}`}
                  className="ghost-btn"
                  style={{
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                    DEEP READ
                  </div>
                  <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                    {slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer
          style={{
            marginTop: 40, paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, letterSpacing: '0.1em',
          }}
        >
          <span>NO. 045 &nbsp;·&nbsp; TOOL 09 OF 09</span>
          <Link
            to="/tools/regional-protocol-generator/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
    </>
  );
}

// ─── ProtocolSection — reusable section panel ────────────────────────────
// Each section of the synthesized document is a panel with a mono kicker,
// a colored left border (sage for "context" sections, sienna for "action"
// sections), a title, and arbitrary children. The pattern matches the
// WHAT-IT-IS / WHAT-TO-DO panels in IGUSelfCheck but is parameterized so
// the document can stack as many sections as the synthesis produces.
function ProtocolSection({ T, fontDisplay, fontBody, fontMono, kicker, kickerColor, title, borderColor, children, revealClass }) {
  return (
    <div
      className={revealClass}
      style={{
        background: T.surface,
        borderLeft: `2px solid ${borderColor}`,
        padding: '24px 28px',
        marginBottom: 20,
      }}
    >
      <div
        style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: kickerColor,
          marginBottom: 8,
        }}
      >
        {kicker}
      </div>
      <h3
        className="section-title"
        style={{
          fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
          lineHeight: 1.15, letterSpacing: '-0.02em',
          margin: '0 0 16px',
          color: T.ink,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}
